import { Button, Modal } from "antd";
import { MessageInstance } from "antd/es/message/interface";
import saveAs from "file-saver";
import JSZip from "jszip";
import { sendMessageToParent } from "./iframeUtils";
import { imageElement } from "../types/ForensicReconstruction/ImageGrid";
import { videoElement } from "../types/ForensicReconstruction/VideoGrid";
import { getImageUrlFromSensorDataFile } from "./detectionUtils";
import { Data } from "@xompass/web-sdk";
import { SensorDataFile } from "../types/SensorData/sensorData";
import { strRemoveAccents } from "./stringUtils";

export async function errorHandling(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fn: () => Promise<any>,
  loadingMessage: string,
  successMessage: string,
  messageApi: MessageInstance,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  general: any,
): Promise<{ success: boolean; response: any }> {
  messageApi.loading(loadingMessage, 0);
  let success = false;
  let response: any;
  await fn()
    .then((result) => {
      response = result;
      setTimeout(messageApi.destroy, 0);
      messageApi.success(successMessage, 2.5);
      success = true;
    })
    .catch((e) => {
      setTimeout(messageApi.destroy, 0);
      console.log(e.status, e.details, e.code);
      if (e.status >= 500) {
        messageApi.error({
          content: (
            <>
              <b>{`Error ${e.status}: `}</b>
              {`${
                general.Errors[`error${e.status}`]
                  ? general.Errors[`error${e.status}`]
                  : general.Errors.generic
              }`}
            </>
          ),
          duration: 5,
        });
      } else {
        messageApi.warning({
          content: (
            <>
              <b>{`Error ${e.status}: `}</b>
              {`${
                general.Errors[`error${e.status}`]
                  ? general.Errors[`error${e.status}`]
                  : general.Errors.generic
              }`}
            </>
          ),
          duration: 5,
        });
        if (e.status === 401) {
          Modal.error({
            title: `Error 401: ${general.Errors.error401}`,
            content: <div>{general.Errors.error401Description}</div>,
            footer: (
              <Button
                danger
                onClick={() => {
                  sendMessageToParent({
                    type: "SESSION_EXPIRED",
                    payload: "SESSION_EXPIRED",
                  });
                }}
              >
                {general.login}
              </Button>
            ),
            centered: true,
            className: "error-modal",
          });
        } else if (e.status === 403) {
          Modal.error({
            title: `Error 403: ${general.Errors.error403}`,
            content: <div>{general.Errors.error403Description}</div>,
            footer: (
              <Button danger onClick={() => Modal.destroyAll()}>
                {general.close}
              </Button>
            ),
            centered: true,
            className: "error-modal",
          });
        }
      }
    });
  return { success, response };
}

// eslint-disable-next-line @typescript-eslint/ban-types
export const debounce = (fn: Function, ms = 1000) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return function (this: any, ...args: any[]) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), ms);
  };
};

export const pause = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export function isImageElement(
  arg: imageElement | videoElement,
): arg is imageElement {
  return (arg as imageElement).imgUrl !== undefined;
}

export async function downloadImagesAsZip(
  links: {
    imgUrl: string;
    sensorName: string;
    assetName: string;
    date: number;
  }[],
) {
  const zip = new JSZip();
  let count = 0;
  const zipFilename = "vsaas_selected_images.zip";
  links.forEach(async ({ imgUrl, sensorName, assetName, date }) => {
    const filename = `${assetName
      .normalize("NFD")
      .toUpperCase()
      .replace(/[\u0300-\u036f]/g, "")}_${sensorName
      .normalize("NFD")
      .toUpperCase()
      .replace(/[\u0300-\u036f]/g, "")}_${
      new Date(date)
        .toISOString()
        .replace("T", "_")
        .replaceAll(":", "-")
        .split(".")[0]
    }.jpg`;
    const urlBlob = await fetch(imgUrl)
      .then((r) => r.blob())
      .then((blobFile) => {
        return new File([blobFile], filename, { type: "image/jpg" });
      });
    zip.file(filename, urlBlob, { binary: true });
    count++;
    if (count == links.length) {
      zip.generateAsync({ type: "blob" }).then(function (content) {
        saveAs(content, zipFilename);
      });
    }
  });
}

export function downloadDetectionImage(sensorId: string, detectionData: Data) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const image: SensorDataFile = (detectionData.content as any).files.image[0];
  const url = getImageUrlFromSensorDataFile(sensorId, image);
  saveAs(url, image.originalFilename ? image.originalFilename : "image.jpg");
}

export function downloadRawData(sensorName: string, data: Data) {
  const ts = data.from?.getTime();
  sensorName = strRemoveAccents(sensorName).replace(/ /g, "_");

  const filename = sensorName + "_" + ts + ".json";

  const blob = new Blob([JSON.stringify(data.content, null, 2)], {
    type: "text/json;charset=utf-8",
  });

  const link = document.createElement("a");
  link.style.display = "none";
  link.href = window.URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// eslint-disable-next-line @typescript-eslint/ban-types, @typescript-eslint/no-explicit-any
export function groupBy(list: any[], keyGetter: Function) {
  const map = new Map();
  list.forEach((item) => {
    const key = keyGetter(item);
    const collection = map.get(key);
    if (!collection) {
      map.set(key, [item]);
    } else {
      collection.push(item);
    }
  });
  return map;
}

export function compareUrls(url1: string, url2: string) {
  if (Math.abs(url1.length - url2.length) < 512) {
    const selectedUrl = url1
      .match("(.*)[?]access_token.*(&datasourceName.*)")
      ?.slice(1, 3)
      ?.join("");
    const url = url2
      .match("(.*)[?]access_token.*(&datasourceName.*)")
      ?.slice(1, 3)
      ?.join("");
    return selectedUrl === url;
  } else {
    return url1 === url2;
  }
}

export function uuidv4() {
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
    (
      +c ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))
    ).toString(16),
  );
}

export const IMAGE_NOT_FOUND =
  "data:image/png;base64,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";
